import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Col,
  Form,
  ListGroup,
  Nav,
  Row,
  Spinner,
  Tab
} from 'react-bootstrap';
import careReferralCatalogService from '../../services/careReferralCatalogService';
import carePartnerService from '../../services/carePartnerService';
import consultationCareReferralService from '../../services/consultationCareReferralService';
import {
  CARE_REFERRAL_CATEGORIES,
  MEDICAL_COVERAGE_TYPES,
  buildEmptyReferralLineMetadata,
  getCareReferralCategoryConfig
} from '../../constants/careReferralCategories';
import { partnerTypeForCategory } from '../../constants/carePartnerTypes';
import {
  REFERRAL_TABS,
  TAB_OPTICAL,
  getClinicalHint,
  getClinicalInformationForCategory,
  getClinicalPlaceholder,
  getTabEmptyMessage,
  getViewPrintLabel,
  matchesCategory,
  normalizeCategory,
  parseCareReferralsResponse,
  referralMetaLine
} from './utils/careReferralFormat';
import ConsultationCareReferralsViewModal from './ConsultationCareReferralsViewModal';
import { ClinicalButton, ClinicalButtonStack } from './ui';
import './ConsultationCareReferralsPanel.css';

/**
 * Orientations et prises en charge partenaires depuis la consultation.
 */
export default function ConsultationCareReferralsPanel({
  consultation,
  onSuccess,
  embedded = false,
  readOnly = false
}) {
  const cid = consultation?.id;
  const [activeTab, setActiveTab] = useState(REFERRAL_TABS[0]);
  const [coverageType, setCoverageType] = useState(MEDICAL_COVERAGE_TYPES.SMIE.id);
  const [search, setSearch] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [lines, setLines] = useState([]);
  const [clinicalHospital, setClinicalHospital] = useState('');
  const [clinicalOphthalmology, setClinicalOphthalmology] = useState('');
  const [clinicalPediatrics, setClinicalPediatrics] = useState('');
  const [clinicalOptical, setClinicalOptical] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewReferrals, setViewReferrals] = useState([]);
  const [viewCategory, setViewCategory] = useState(REFERRAL_TABS[0]);
  const [viewClinicalInformation, setViewClinicalInformation] = useState('');
  const [viewPreviewUnsaved, setViewPreviewUnsaved] = useState(false);
  const [loadingView, setLoadingView] = useState(false);

  const clinicalStateByTab = {
    [CARE_REFERRAL_CATEGORIES.HOSPITAL_REFERRAL.id]: [clinicalHospital, setClinicalHospital],
    [CARE_REFERRAL_CATEGORIES.OPHTHALMOLOGY.id]: [clinicalOphthalmology, setClinicalOphthalmology],
    [CARE_REFERRAL_CATEGORIES.PEDIATRICS.id]: [clinicalPediatrics, setClinicalPediatrics],
    [CARE_REFERRAL_CATEGORIES.OPTICAL.id]: [clinicalOptical, setClinicalOptical]
  };

  const loadReferrals = useCallback(async () => {
    if (!cid) return;
    setLoadingReferrals(true);
    setError('');
    try {
      const res = await consultationCareReferralService.list(cid);
      if (res.success) {
        const parsed = parseCareReferralsResponse(res);
        setLines(parsed.referrals);
        setClinicalHospital(parsed.clinical_information_hospital);
        setClinicalOphthalmology(parsed.clinical_information_ophthalmology);
        setClinicalPediatrics(parsed.clinical_information_pediatrics);
        setClinicalOptical(parsed.clinical_information_optical);
        setDirty(false);
      }
    } catch (e) {
      setError(e.message || 'Chargement des orientations impossible.');
    } finally {
      setLoadingReferrals(false);
    }
  }, [cid]);

  useEffect(() => {
    if (!cid) return;
    setLines([]);
    setClinicalHospital('');
    setClinicalOphthalmology('');
    setClinicalPediatrics('');
    setClinicalOptical('');
    setSearch('');
    setDirty(false);
    loadReferrals();
  }, [cid, loadReferrals]);

  useEffect(() => {
    if (!cid) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      try {
        const params = {
          category: activeTab,
          coverage_type: coverageType,
          is_active: 'true'
        };
        if (search.trim()) params.search = search.trim();
        const res = await careReferralCatalogService.list(params);
        if (!cancelled && res.success) {
          setCatalog(Array.isArray(res.data) ? res.data : []);
        } else if (!cancelled) {
          setCatalog([]);
        }
      } catch {
        if (!cancelled) setCatalog([]);
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cid, activeTab, coverageType, search]);

  useEffect(() => {
    if (!cid) return undefined;
    const partnerType = partnerTypeForCategory(activeTab);
    if (!partnerType) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await carePartnerService.list({ partner_type: partnerType, is_active: 'true' });
        if (!cancelled && res.success) {
          setPartners(Array.isArray(res.data) ? res.data : []);
        } else if (!cancelled) {
          setPartners([]);
        }
      } catch {
        if (!cancelled) setPartners([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cid, activeTab]);

  const linesForTab = useMemo(
    () => lines.filter((l) => matchesCategory(l.category, activeTab)),
    [lines, activeTab]
  );

  const selectedIds = useMemo(
    () => new Set(linesForTab.map((l) => l.catalog_referral_id)),
    [linesForTab]
  );

  const addReferral = (item) => {
    if (lines.some((l) => l.catalog_referral_id === item.id)) return;
    const category = normalizeCategory(item.category || activeTab);
    setLines((prev) => [
      ...prev,
      {
        catalog_referral_id: item.id,
        referral_name: item.name,
        category,
        coverage_type: item.coverage_type,
        partner_id: item.default_partner_id || '',
        partner_name: item.partner_name || '',
        metadata: category === TAB_OPTICAL ? buildEmptyReferralLineMetadata(category) : {},
        catalog_metadata: item.metadata || {}
      }
    ]);
    setDirty(true);
    setError('');
  };

  const removeLine = (catalogReferralId) => {
    setLines((prev) => prev.filter((l) => l.catalog_referral_id !== catalogReferralId));
    setDirty(true);
  };

  const updateLinePartner = (catalogReferralId, partnerId) => {
    const partner = partners.find((p) => String(p.id) === String(partnerId));
    setLines((prev) =>
      prev.map((l) =>
        l.catalog_referral_id === catalogReferralId
          ? {
              ...l,
              partner_id: partnerId || '',
              partner_name: partner?.name || l.partner_name
            }
          : l
      )
    );
    setDirty(true);
  };

  const updateLineMetadata = (catalogReferralId, key, value) => {
    setLines((prev) =>
      prev.map((l) =>
        l.catalog_referral_id === catalogReferralId
          ? { ...l, metadata: { ...l.metadata, [key]: value } }
          : l
      )
    );
    setDirty(true);
  };

  const handleOpenView = async () => {
    const tabLines = lines.filter((l) => matchesCategory(l.category, activeTab));
    if (tabLines.length === 0) {
      setError(getTabEmptyMessage(activeTab));
      return;
    }
    setLoadingView(true);
    setError('');
    try {
      setViewCategory(activeTab);
      const parsedClinical = {
        clinical_information_hospital: clinicalHospital,
        clinical_information_ophthalmology: clinicalOphthalmology,
        clinical_information_pediatrics: clinicalPediatrics,
        clinical_information_optical: clinicalOptical
      };
      if (dirty) {
        setViewReferrals(tabLines);
        setViewClinicalInformation(getClinicalInformationForCategory(parsedClinical, activeTab));
        setViewPreviewUnsaved(true);
        setShowViewModal(true);
        return;
      }
      const res = await consultationCareReferralService.list(cid);
      if (res.success) {
        const parsed = parseCareReferralsResponse(res);
        const filtered = parsed.referrals.filter((r) => matchesCategory(r.category, activeTab));
        if (filtered.length === 0) {
          setError(getTabEmptyMessage(activeTab, true));
          return;
        }
        setViewReferrals(filtered);
        setViewClinicalInformation(getClinicalInformationForCategory(parsed, activeTab));
        setViewPreviewUnsaved(false);
        setShowViewModal(true);
      } else {
        setError(res.message || 'Impossible de charger les orientations.');
      }
    } catch (e) {
      setError(e.message || 'Impossible d\'ouvrir l\'aperçu.');
    } finally {
      setLoadingView(false);
    }
  };

  const handleSave = async () => {
    if (!cid) return;
    setError('');
    setBusy(true);
    try {
      const payload = lines.map((l) => ({
        catalog_referral_id: l.catalog_referral_id,
        partner_id: l.partner_id || null,
        metadata: l.metadata || {}
      }));
      const res = await consultationCareReferralService.sync(
        cid,
        payload,
        clinicalHospital.trim() || null,
        clinicalOphthalmology.trim() || null,
        clinicalPediatrics.trim() || null,
        clinicalOptical.trim() || null
      );
      if (!res.success) {
        throw new Error(res.message || 'Enregistrement refusé.');
      }
      const parsed = parseCareReferralsResponse(res);
      setLines(parsed.referrals);
      setClinicalHospital(parsed.clinical_information_hospital);
      setClinicalOphthalmology(parsed.clinical_information_ophthalmology);
      setClinicalPediatrics(parsed.clinical_information_pediatrics);
      setClinicalOptical(parsed.clinical_information_optical);
      setDirty(false);
      onSuccess?.();
    } catch (e) {
      setError(e.message || 'Enregistrement des orientations impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (!cid) return null;

  const tabConfig = getCareReferralCategoryConfig(activeTab);
  const [activeClinicalValue, setActiveClinicalValue] =
    clinicalStateByTab[activeTab] || clinicalStateByTab[REFERRAL_TABS[0]];
  const orderLineFields = tabConfig?.orderLineFields || [];

  return (
    <div
      className={`care-referrals-panel${embedded ? ' care-referrals-panel--embedded' : ''}${
        readOnly ? ' care-referrals-panel--readonly' : ''
      }`}
    >
      {!embedded ? (
        <p className="care-referrals-panel__intro">
          Orientations vers les partenaires (hôpital, ophtalmo, pédiatrie, lunettes). Filtrez SMIE / CMC,
          choisissez le partenaire si besoin, puis enregistrez.
        </p>
      ) : null}

      {error ? (
        <Alert variant="danger" className="py-2 small mb-2">
          {error}
        </Alert>
      ) : null}

      {loadingReferrals ? (
        <div className="text-center py-2">
          <Spinner animation="border" size="sm" />
        </div>
      ) : null}

      <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
        <Nav variant="tabs" className="care-referrals-panel__tabs mb-2" role="tablist">
          {REFERRAL_TABS.map((tabId) => (
            <Nav.Item key={tabId}>
              <Nav.Link eventKey={tabId}>
                {getCareReferralCategoryConfig(tabId)?.labelPlural}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <Tab.Content>
          <div className="mb-2">
            <div className="care-referrals-panel__filters d-flex flex-wrap gap-1 mb-2">
              {Object.values(MEDICAL_COVERAGE_TYPES).map((cov) => (
                <ClinicalButton
                  key={cov.id}
                  compact
                  variant={coverageType === cov.id ? 'primary' : 'secondary'}
                  type="button"
                  onClick={() => setCoverageType(cov.id)}
                  disabled={busy}
                >
                  {cov.label}
                </ClinicalButton>
              ))}
            </div>
            <Form.Control
              size="sm"
              type="search"
              placeholder={`Rechercher dans le catalogue ${tabConfig?.labelPlural?.toLowerCase() || ''}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="care-referrals-panel__catalog border rounded mb-3">
            {loadingCatalog ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : catalog.length === 0 ? (
              <p className="small text-muted mb-0 p-3">Aucun acte trouvé pour ces filtres.</p>
            ) : (
              <ListGroup variant="flush">
                {catalog.map((item) => {
                  const added = selectedIds.has(item.id);
                  const meta = referralMetaLine({
                    category: item.category,
                    partner_name: item.partner_name,
                    metadata: item.metadata
                  });
                  return (
                    <ListGroup.Item
                      key={item.id}
                      action={!added}
                      disabled={added || busy}
                      className={`care-referrals-panel__catalog-item py-2 ${added ? 'is-added' : ''}`}
                      onClick={() => !added && addReferral(item)}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <strong className="small">{item.name}</strong>
                          {meta ? (
                            <div className="care-referrals-panel__meta text-muted">{meta}</div>
                          ) : null}
                          {item.description ? (
                            <div className="care-referrals-panel__meta text-muted">{item.description}</div>
                          ) : null}
                        </div>
                        {added ? (
                          <Badge bg="secondary" className="fw-normal">
                            Ajouté
                          </Badge>
                        ) : (
                          <span className="small text-primary">+ Ajouter</span>
                        )}
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </div>

          {linesForTab.length > 0 ? (
            <div className="mb-3">
              <div className="small fw-semibold text-muted mb-2">
                Demandes — {tabConfig?.labelPlural} ({linesForTab.length})
              </div>
              <ListGroup variant="flush" className="care-referrals-panel__demand-list border rounded">
                {linesForTab.map((line) => (
                  <ListGroup.Item key={line.catalog_referral_id} className="py-2">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div className="small">
                        <strong>{line.referral_name}</strong>
                        {referralMetaLine(line) ? (
                          <div className="care-referrals-panel__meta text-muted">
                            {referralMetaLine(line)}
                          </div>
                        ) : null}
                      </div>
                      <ClinicalButton
                        variant="danger"
                        compact
                        type="button"
                        disabled={busy}
                        onClick={() => removeLine(line.catalog_referral_id)}
                      >
                        Retirer
                      </ClinicalButton>
                    </div>
                    {partners.length > 0 ? (
                      <Form.Select
                        size="sm"
                        className="mb-2"
                        value={line.partner_id || ''}
                        onChange={(e) => updateLinePartner(line.catalog_referral_id, e.target.value)}
                        disabled={busy}
                        aria-label="Partenaire"
                      >
                        <option value="">— Partenaire —</option>
                        {partners.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Form.Select>
                    ) : null}
                    {orderLineFields.length > 0 ? (
                      <Row className="g-2">
                        {orderLineFields.map((field) => (
                          <Col key={field.key} xs={6} md={4}>
                            <Form.Label className="small mb-0">{field.label}</Form.Label>
                            <Form.Control
                              size="sm"
                              value={line.metadata?.[field.key] || ''}
                              onChange={(e) =>
                                updateLineMetadata(line.catalog_referral_id, field.key, e.target.value)
                              }
                              disabled={busy}
                            />
                          </Col>
                        ))}
                      </Row>
                    ) : null}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          ) : (
            <p className="small text-muted mb-3">
              Aucune demande pour cet onglet — parcourez le catalogue ci-dessus.
            </p>
          )}

          <Form.Group className="care-referrals-panel__clinical mb-0 mt-3">
            <Form.Label className="small fw-semibold">Renseignement clinique</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              placeholder={getClinicalPlaceholder(activeTab)}
              value={activeClinicalValue}
              onChange={(e) => {
                setActiveClinicalValue(e.target.value);
                setDirty(true);
              }}
              disabled={busy}
            />
            <Form.Text muted className="small">
              {getClinicalHint(activeTab)}
            </Form.Text>
          </Form.Group>
        </Tab.Content>
      </Tab.Container>

      {lines.length > 0 ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2 mt-2">
          <Badge bg="light" text="dark" className="border fw-normal">
            Total : {lines.length} orientation{lines.length > 1 ? 's' : ''}
          </Badge>
          {dirty ? (
            <span className="small text-warning">Modifications non enregistrées</span>
          ) : null}
        </div>
      ) : null}

      <ClinicalButtonStack>
        <ClinicalButton
          variant="view"
          icon="eye"
          block
          loading={loadingView}
          disabled={busy || linesForTab.length === 0}
          onClick={handleOpenView}
        >
          Voir &amp; imprimer {getViewPrintLabel(activeTab)}
        </ClinicalButton>
        {!readOnly ? (
        <ClinicalButton
          variant="save"
          block
          loading={busy}
          disabled={!dirty}
          onClick={handleSave}
        >
          Enregistrer les orientations
        </ClinicalButton>
        ) : null}
      </ClinicalButtonStack>

      <ConsultationCareReferralsViewModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        consultation={consultation}
        referrals={viewReferrals}
        category={viewCategory}
        clinicalInformation={viewClinicalInformation}
        previewUnsaved={viewPreviewUnsaved}
      />
    </div>
  );
}
